import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
type BundleId = 'single' | 'duo' | 'poly';
export function SubscriptionBox() {
  const {
    t
  } = useLanguage();
  const [selectedBundle, setSelectedBundle] = useState<BundleId>('poly');
  const [isSubscription, setIsSubscription] = useState(false);
  const bundles: {
    id: BundleId;
    isFeatured: boolean;
  }[] = [{
    id: 'single',
    isFeatured: false
  }, {
    id: 'duo',
    isFeatured: false
  }, {
    id: 'poly',
    isFeatured: true
  }];
  return <section className="py-24 bg-white border-y-2 border-black overflow-hidden" data-id="element-395">
      <div className="max-w-6xl mx-auto px-6" data-id="element-396">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-397">
          <div className="inline-block px-4 py-1 border border-black text-xs font-mono tracking-wider uppercase mb-6" data-id="element-398">
            {t('subscription.badge')}
          </div>
          <h2 className="text-5xl md:text-7xl font-black text-black mb-6 uppercase tracking-tighter leading-none" style={{
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontStretch: 'condensed'
        }} data-id="element-399">
            {t('subscription.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-400">
            {t('subscription.subtitle')}
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-12 items-start" data-id="element-401">
          {/* Left Side: Product Image */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="w-full lg:w-2/5 lg:sticky lg:top-24" data-id="element-402">
            <div className="relative aspect-square w-full max-w-md mx-auto border-2 border-black bg-gray-100 overflow-hidden group" data-id="element-403">
              <img src="https://cdn.magicpatterns.com/uploads/pFn7rSHg2JGfHcZB6MmrLd/IMG_3601.png" alt="NeuroSpice Poly-Pack" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700" data-id="element-404" />
            </div>
          </motion.div>

          {/* Right Side: Bundle Selection */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="w-full lg:w-3/5 flex flex-col gap-4" data-id="element-405">
            {/* Bundle Cards */}
            <div className="flex flex-col gap-3" data-id="element-406">
              {bundles.map(bundle => {
              const isSelected = selectedBundle === bundle.id;
              return <button key={bundle.id} onClick={() => setSelectedBundle(bundle.id)} className={`relative p-6 border-2 transition-all duration-200 text-left ${isSelected ? 'border-black bg-black text-white' : 'border-gray-300 bg-white text-black hover:border-black'}`} data-id="element-407">
                    {/* Featured Badge */}
                    {bundle.isFeatured && <div className={`absolute -top-3 right-6 px-3 py-1 text-xs font-black uppercase tracking-wider ${isSelected ? 'bg-white text-black' : 'bg-black text-white'}`} data-id="element-408">
                        {t('subscription.bundle.poly.badge')}
                      </div>}

                    <div className="flex justify-between items-start gap-4" data-id="element-409">
                      {/* Left: Bundle Info */}
                      <div className="flex-1" data-id="element-410">
                        <div className="flex items-baseline gap-3 mb-1" data-id="element-411">
                          <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tight" style={{
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontStretch: 'condensed'
                      }} data-id="element-412">
                            {t(`subscription.bundle.${bundle.id}.name`)}
                          </h3>
                          <span className={`text-xs font-mono uppercase tracking-wider ${isSelected ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-413">
                            {t(`subscription.bundle.${bundle.id}.contents`)}
                          </span>
                        </div>
                        <p className={`text-sm ${isSelected ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-414">
                          {t(`subscription.bundle.${bundle.id}.tagline`)}
                        </p>
                      </div>

                      {/* Right: Pricing */}
                      <div className="text-right flex-shrink-0" data-id="element-415">
                        <div className="text-xl font-black font-mono leading-none mb-1" data-id="element-416">
                          {t(`subscription.bundle.${bundle.id}.price`)}
                        </div>
                        <div className={`flex flex-col items-end ${isSelected ? 'text-white' : 'text-black'}`} data-id="element-417">
                          <span className="text-3xl md:text-4xl font-black font-mono leading-none" data-id="element-418">
                            {t(`subscription.bundle.${bundle.id}.perServing`)}
                          </span>
                          <span className={`text-[10px] font-mono uppercase tracking-widest mt-1 ${isSelected ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-419">
                            {t('subscription.perServing.label')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>;
            })}
            </div>

            {/* Subscribe & Save Toggle */}
            <div className="mt-2 border-2 border-gray-300 p-4 flex items-center justify-between gap-4" data-id="element-420">
              <div className="flex-1" data-id="element-421">
                <div className="flex items-center gap-2 mb-1" data-id="element-422">
                  <span className="font-black text-sm uppercase tracking-wider" data-id="element-423">
                    {t('subscription.subscribe')}
                  </span>
                  <span className="text-[10px] font-black px-2 py-0.5 bg-black text-white uppercase tracking-wider" data-id="element-424">
                    {t('subscription.save')}
                  </span>
                </div>
                <p className="text-xs text-gray-600" data-id="element-425">
                  {t('subscription.subscribe.description')}
                </p>
              </div>
              {/* Toggle Switch */}
              <button onClick={() => setIsSubscription(!isSubscription)} role="switch" aria-checked={isSubscription} className={`relative w-14 h-7 border-2 border-black transition-colors duration-200 flex-shrink-0 ${isSubscription ? 'bg-black' : 'bg-white'}`} data-id="element-426">
                <motion.div animate={{
                x: isSubscription ? 26 : 0
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} className={`absolute top-0 left-0 w-6 h-6 ${isSubscription ? 'bg-white' : 'bg-black'}`} data-id="element-427" />
              </button>
            </div>

            {/* CTA */}
            <button className="w-full py-5 bg-black text-white font-black text-lg hover:bg-gray-900 border-2 border-black transition-all duration-300 uppercase tracking-widest shadow-lg hover:shadow-xl hover:scale-[1.01] mt-4" data-id="element-428">
              {t('subscription.cta')}
            </button>

            {/* Notes */}
            <div className="text-center space-y-1 mt-2" data-id="element-429">
              <p className="text-xs text-gray-500" data-id="element-430">{t('subscription.note')}</p>
              <p className="text-xs font-bold text-black uppercase tracking-wider" data-id="element-431">
                {t('subscription.impact')}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}